import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Link } from '@hivebrite/design-system/Link';

import { getCategories } from 'hb-react/backoffice/components/CategoriesList/requests';
import { type CategoriesResponse } from 'hb-react/backoffice/components/CategoriesList/types';
import { makeConfigUrls } from 'hb-react/backoffice/components/CategoriesList/utils/configUrls';
import { PER_PAGE } from 'hb-react/shared/components/ReactSelect/constants';
import ReactSelectAdaptive from 'hb-react/shared/components/ReactSelect/ReactSelectAdaptive';
import { type QueryFn } from 'hb-react/shared/components/ReactSelect/ReactSelectAdaptive/types';
import { type ReactSelectCategoriesProps } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/types';
import { categoriesToOptions } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/utils/categoriesToOptions';

const extractData = (response: CategoriesResponse) => response.data;
const shouldUseAsync = (defaultOptionsQueryResponse: CategoriesResponse) => {
  if (defaultOptionsQueryResponse.total === null) {
    return false;
  }

  return (
    defaultOptionsQueryResponse.total > defaultOptionsQueryResponse.data.length
  );
};

/**
 * NOTE: this component uses a backoffice endpoint to fetch categories
 */
export const ReactSelectCategories = ({
  categorizedType,
  createCategoryPath,
  isMulti,
  label = __('Filter by category'),
  networkId,
  onChange,
  placeholder = __('Search and select a category'),
  topicId,
  availableForNetwork,
  value,
  ...otherReactSelectProps
}: ReactSelectCategoriesProps) => {
  const queryClient = useQueryClient();

  const queryFn = useCallback<QueryFn<CategoriesResponse>>(
    ({ searchText, ids }, signal) => {
      const configUrls = makeConfigUrls({
        networkId,
        type: categorizedType,
        topicId,
        availableForNetwork,
        page: 1,
        hasPagination: true,
        perPage: ids ? ids.length : PER_PAGE,
        searchKeyword: searchText,
        ids: ids,
      });

      return getCategories(configUrls, signal);
    },
    [networkId, categorizedType, topicId, availableForNetwork],
  );

  const queryKey = useMemo(
    () => [
      networkId,
      topicId,
      'categories',
      categorizedType,
      availableForNetwork,
    ],
    [networkId, topicId, categorizedType, availableForNetwork],
  );

  // Invalidate categories query when tab regains focus (user returns from creating category in new tab)
  useEffect(() => {
    if (!createCategoryPath) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [queryClient, queryKey, createCategoryPath]);

  const noOptionsMessage = useCallback(
    ({ inputValue }: { inputValue: string }) => {
      const message = inputValue
        ? s__('NetworkCategories|No categories found for "%{query}"').i__({
            query: inputValue,
          })
        : s__('NetworkCategories|No categories available');

      if (!createCategoryPath) {
        return message;
      }

      const categoryUrl = `${window.location.origin}${createCategoryPath}`;

      return (
        <Box
          flexDirection="column"
          alignItems="center"
          p={3}
          textAlign="center"
        >
          <Box mb={2} font="body-regular-dove">
            {message}
          </Box>
          <Link to={categoryUrl} target="_blank">
            {s__('NetworkCategories|Create a category')}
          </Link>
        </Box>
      );
    },
    [createCategoryPath],
  );

  return (
    <ReactSelectAdaptive
      queryKey={queryKey}
      queryFn={queryFn}
      extractData={extractData}
      shouldUseAsync={shouldUseAsync}
      dataToOptions={categoriesToOptions}
      isMulti={isMulti}
      label={label}
      placeholder={placeholder}
      onChange={onChange}
      value={value}
      noOptionsMessage={noOptionsMessage}
      {...otherReactSelectProps}
    />
  );
};

export default ReactSelectCategories;
